import {
  getGridSingleSelectOperators,
  getGridStringOperators,
} from '@mui/x-data-grid';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import type {
  DiamAuditAction,
  DiamAuditLogFilter,
  DiamAuditResourceType,
  DiamIdentityFilter,
  DiamIdentityType,
  DiamInvitationFilter,
  DiamInvitationStatus,
  DiamPageSortField,
  DiamRoleFilter,
  DiamTeamFilter,
  DiamTeamRoleFilter,
} from './diamApi';

export const DIAM_GRID_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export const diamGridStringFilterOperators = getGridStringOperators().filter(
  operator => ['contains'].includes(operator.value),
);

export const diamGridSingleSelectFilterOperators =
  getGridSingleSelectOperators().filter(operator => operator.value === 'is');

const SORT_FIELD_ALIASES: Record<string, string> = {
  createdAt: 'createdDate',
  updatedAt: 'lastModifiedDate',
  teamName: 'teamId',
};

export function toDiamSortFields(
  sortModel: GridSortModel,
  fallback: DiamPageSortField[] = [],
): DiamPageSortField[] {
  const entry = sortModel[0];
  if (!entry?.field || !entry.sort) {
    return fallback;
  }

  const field = SORT_FIELD_ALIASES[entry.field] ?? entry.field;
  return [
    {
      field,
      direction: entry.sort === 'desc' ? 'DESC' : 'ASC',
    },
  ];
}

export function getQuickFilterValue(filterModel: GridFilterModel): string {
  return filterModel.quickFilterValues?.[0]?.trim() ?? '';
}

export interface DiamGridFilterItem {
  field: string;
  value: unknown;
}

export function getDiamGridFilterItems(
  filterModel: GridFilterModel,
): DiamGridFilterItem[] {
  return filterModel.items
    .filter(
      item =>
        item.value !== undefined && item.value !== null && item.value !== '',
    )
    .map(item => ({ field: item.field, value: item.value }));
}

function getFilterItemValue(
  filterModel: GridFilterModel,
  field: string,
): unknown {
  return filterModel.items.find(item => item.field === field)?.value;
}

const INVITATION_STATUS_BY_UI: Record<string, DiamInvitationStatus> = {
  pending: 'PENDING',
  accepted: 'ACCEPTED',
  revoked: 'REVOKED',
  expired: 'EXPIRED',
};

function applyColumnFilters<T extends object>(
  filter: T,
  items: DiamGridFilterItem[],
  handlers: Record<string, (value: unknown, target: T) => void>,
): T {
  for (const { field, value } of items) {
    const handler = handlers[field];
    if (handler) {
      handler(value, filter);
    }
  }
  return filter;
}

export function buildTeamsPageFilter(
  filterModel: GridFilterModel,
): DiamTeamFilter {
  const filter: DiamTeamFilter = {};
  const quickName = getQuickFilterValue(filterModel);
  if (quickName) {
    filter.name = quickName;
  }

  return applyColumnFilters(filter, getDiamGridFilterItems(filterModel), {
    name: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.name = value.trim();
      }
    },
  });
}

export function buildIdentitiesPageFilter(
  baseFilter: DiamIdentityFilter,
  filterModel: GridFilterModel,
): DiamIdentityFilter {
  const filter: DiamIdentityFilter = { ...baseFilter };
  const search = getQuickFilterValue(filterModel);
  if (search) {
    filter.search = search;
  }

  return applyColumnFilters(filter, getDiamGridFilterItems(filterModel), {
    name: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.search = value.trim();
      }
    },
    email: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.search = value.trim();
      }
    },
    type: (value, target) => {
      if (value === 'Human') {
        target.type = 'HUMAN';
      } else if (value === 'M2M') {
        target.type = 'M2M';
      } else if (value === 'HUMAN' || value === 'M2M') {
        target.type = value as DiamIdentityType;
      }
    },
    accessCategory: (value, target) => {
      if (value === 'Internal' || value === true || value === 'true') {
        target.isInternal = true;
      } else if (value === 'External' || value === false || value === 'false') {
        target.isInternal = false;
      }
    },
  });
}

export function buildRolesPageFilter(
  baseFilter: DiamRoleFilter,
  filterModel: GridFilterModel,
): DiamRoleFilter {
  const filter: DiamRoleFilter = { ...baseFilter };
  const search = getQuickFilterValue(filterModel);
  if (search) {
    filter.search = search;
  }

  return applyColumnFilters(filter, getDiamGridFilterItems(filterModel), {
    name: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.search = value.trim();
      }
    },
  });
}

export function buildTeamRolesPageFilter(
  baseFilter: DiamTeamRoleFilter,
  filterModel: GridFilterModel,
): DiamTeamRoleFilter {
  const filter: DiamTeamRoleFilter = { ...baseFilter };
  const search = getQuickFilterValue(filterModel);
  if (search) {
    filter.search = search;
  }

  return applyColumnFilters(filter, getDiamGridFilterItems(filterModel), {
    name: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.search = value.trim();
      }
    },
  });
}

export function buildInvitationsPageFilter(
  filterModel: GridFilterModel,
): DiamInvitationFilter {
  const filter: DiamInvitationFilter = {};
  const email = getQuickFilterValue(filterModel);
  if (email) {
    filter.email = email;
  }

  const items = getDiamGridFilterItems(filterModel);
  applyColumnFilters(filter, items, {
    email: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.email = value.trim();
      }
    },
    status: (value, target) => {
      if (typeof value !== 'string') {
        return;
      }
      const status = INVITATION_STATUS_BY_UI[value.toLowerCase()];
      if (status) {
        target.status = status;
      }
    },
  });

  if (!filter.status) {
    const statusValue = getFilterItemValue(filterModel, 'status');
    if (typeof statusValue === 'string') {
      const status = INVITATION_STATUS_BY_UI[statusValue.toLowerCase()];
      if (status) {
        filter.status = status;
      }
    }
  }

  return filter;
}

export function buildAuditLogsPageFilter(
  baseFilter: DiamAuditLogFilter,
  filterModel: GridFilterModel,
): DiamAuditLogFilter {
  const filter: DiamAuditLogFilter = { ...baseFilter };
  const createdByName = getQuickFilterValue(filterModel);
  if (createdByName) {
    filter.createdByName = createdByName;
  }

  return applyColumnFilters(filter, getDiamGridFilterItems(filterModel), {
    createdByName: (value, target) => {
      if (typeof value === 'string' && value.trim()) {
        target.createdByName = value.trim();
      }
    },
    action: (value, target) => {
      if (typeof value === 'string' && value) {
        target.actions = [value as DiamAuditAction];
      }
    },
    resourceType: (value, target) => {
      if (typeof value === 'string' && value) {
        target.resourceTypes = [value as DiamAuditResourceType];
      }
    },
  });
}
